// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { StyledBox } from "@steiner/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    openBidDetailsState: state.xOpenBidDetails,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

interface OpenBidsDetailsTotalStyles {
    container?: GridContainerProps;
    fullWidthGridItem?: GridItemProps;
    subtotalLabelGridItem?: GridItemProps;
    subtotalValueGridItem?: GridItemProps;
    titleText?: TypographyProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    wrapper?: InjectableCss;
    styledHr?: InjectableCss;

    // xng-changes
    boxContainer?: InjectableCss;
}

export const openBidsDetailsTotalStyles: OpenBidsDetailsTotalStyles = {};

const styles = openBidsDetailsTotalStyles;

const StyledHr = getStyledWrapper("hr");

const OpenBidsDetailsTotal = ({ openBidDetailsState }: Props) => {
    if (!openBidDetailsState.dataView?.salesOrder || openBidDetailsState.isLoading) {
        return null;
    }

    const order = openBidDetailsState.dataView.salesOrder;
    const shippingAndHandling = Number(order.orderTotals?.freight) + Number(order.orderTotals?.handling);
    const taxAmount = Number(order.orderTotals?.tax) + Number(order.orderTotals?.federalExciseTax);

    return (
        <StyledBox {...styles.boxContainer}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fullWidthGridItem}>
                    <Typography {...styles.titleText}>{translate("Bid Summary")}</Typography>
                </GridItem>
                <GridItem {...styles.fullWidthGridItem} className="separator">
                    <StyledHr {...styles.styledHr} />
                </GridItem>
                <GridItem {...styles.subtotalLabelGridItem}>
                    <Typography {...styles.subtotalLabel}>{translate("Subtotal")}</Typography>
                </GridItem>
                <GridItem {...styles.subtotalValueGridItem}>
                    <Typography {...styles.subtotalValue}>
                        {order.orderHeader.currency.substr(-1)}
                        {order.orderTotals.subtotal}
                    </Typography>
                </GridItem>
                {order.discountAmount > 0 && (
                    <>
                        <GridItem {...styles.subtotalLabelGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("Discounts")}</Typography>
                        </GridItem>
                        <GridItem {...styles.subtotalValueGridItem}>
                            <Typography {...styles.subtotalValue}>
                                {order.orderHeader.currency.substr(-1)}
                                {order.orderHeader.paymentTerms.discount.cashDiscount.amount}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {shippingAndHandling > 0 && (
                    <>
                        <GridItem {...styles.subtotalLabelGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("Shipping & Handling")}</Typography>
                        </GridItem>
                        <GridItem {...styles.subtotalValueGridItem}>
                            <Typography {...styles.subtotalValue}>
                                {order.orderHeader.currency.substr(-1)}
                                {order.orderTotals.handling}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {order.otherCharges > 0 && (
                    <>
                        <GridItem {...styles.subtotalLabelGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("Other Charges")}</Typography>
                        </GridItem>
                        <GridItem {...styles.subtotalValueGridItem}>
                            <Typography {...styles.subtotalValue}>{order.otherChargesDisplay}</Typography>
                        </GridItem>
                    </>
                )}
                {taxAmount > 0 && (
                    <>
                        <GridItem {...styles.subtotalLabelGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("Tax")}</Typography>
                        </GridItem>
                        <GridItem {...styles.subtotalValueGridItem}>
                            <Typography {...styles.subtotalValue}>
                                {order.orderHeader.currency.substr(-1)}
                                {order.orderTotals.tax}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {order.orderHistoryTaxes &&
                    order.orderHistoryTaxes.map(
                        (orderTax: any, index: any) =>
                            orderTax.taxDescription && (
                                <SmallHeadingAndText
                                    key={index}
                                    heading={translate("Tax")}
                                    text={orderTax.taxDescription}
                                    // extendedStyles={styles.infoHeadingAndText}
                                />
                            ),
                    )}
                <GridItem {...styles.subtotalLabelGridItem}>
                    <Typography {...styles.subtotalLabel}>{translate("Total")}</Typography>
                </GridItem>
                <GridItem {...styles.subtotalValueGridItem}>
                    <Typography {...styles.subtotalValue}>
                        {order.orderHeader.currency.substr(-1)}
                        {order.orderTotals.total}
                    </Typography>
                </GridItem>

                <GridItem {...styles.fullWidthGridItem} className="separator">
                    <StyledHr {...styles.styledHr} />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem} className="separator">
                    {createWidgetElement("OpenBidsDetails/OpenBidsDetailsHeader", {
                        showActionsOnly: true,
                        showPrint: false,
                        fields: {},
                    } as any)}
                </GridItem>
            </GridContainer>
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(OpenBidsDetailsTotal),
    definition: {
        displayName: "Open Bids Details Total",
        allowedContexts: ["OpenBidsDetailsPage"],
        group: XNGAGE_ELEMENTS,
    },
};

export default widgetModule;
